import { createFileRoute, Link } from "@tanstack/react-router";
import { useState, type MouseEvent } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  ArrowRight,
  CheckCircle2,
  CreditCard,
  CalendarClock,
  MapPin,
  AlertTriangle,
  ShieldCheck,
  HelpCircle,
  FileCheck2,
  Plus,
  Lightbulb,
  AlertCircle,
  Monitor,
  Banknote,
  Smartphone,
  RefreshCw,
  Lock,
  FileText,
  ClipboardList,
} from "lucide-react";
import { site } from "@/lib/site";
import { SchedulingTroubleshooting, troubleshootingFaq } from "@/components/site/SchedulingTroubleshooting";

export const Route = createFileRoute("/us-visa-scheduling-guide")({
  head: () => ({
    meta: [
      {
        title: "US Visa Scheduling Guide 2026: Portal Login, MRV Fee & Slot Rules (India)",
      },
      {
        name: "description",
        content:
          "Complete 2026 step-by-step guide for usvisascheduling.com in India. How to pay MRV fees via UPI, fix portal login/lockout errors, manage OFC vs Consular slots, and prep for your F-1 interview.",
      },
      {
        property: "og:title",
        content: "US Visa Scheduling Guide 2026: Portal Login, MRV Fee & Slot Rules (India)",
      },
      {
        property: "og:description",
        content:
          "Complete 2026 step-by-step guide for usvisascheduling.com in India. How to pay MRV fees via UPI, fix portal login/lockout errors, manage OFC vs Consular slots, and prep for your F-1 interview.",
      },
      { property: "og:type", content: "article" },
      {
        property: "og:url",
        content: "https://welivdit.com/us-visa-scheduling-guide",
      },
      { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
    ],
    links: [
      {
        rel: "canonical",
        href: "https://welivdit.com/us-visa-scheduling-guide",
      },
    ],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: "US Visa Scheduling Portal Guide 2026: Login, Fee Payment & Slot Rules (India)",
          description:
            "Complete 2026 step-by-step guide for usvisascheduling.com in India. How to pay MRV fees via UPI, fix portal login/lockout errors, manage OFC vs Consular slots, and prep for your F-1 interview.",
          author: { "@type": "Organization", name: "WeLivdIt" },
          publisher: {
            "@type": "Organization",
            name: "WeLivdIt",
            url: "https://welivdit.com",
          },
          mainEntityOfPage: "https://welivdit.com/us-visa-scheduling-guide",
          datePublished: "2026-09-07",
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: [
            ...troubleshootingFaq.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
            {
              "@type": "Question",
              name: "How do I log in to the US visa scheduling portal in India?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Indian applicants must use the official portal at usvisascheduling.com. Create an account with a unique username, verify your email, and set security questions to access the dashboard.",
              },
            },
            {
              "@type": "Question",
              name: "How long does MRV fee activation take via UPI payment?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "UPI payments through the official usvisascheduling portal reflect instantly in most cases. NEFT bank transfers typically take up to 1 business day. Axis Bank cash deposits and waybills routinely take 2 to 4 working days to reflect as 'Paid' on usvisascheduling.com.",
              },
            },
            {
              "@type": "Question",
              name: "Can I update my DS-160 number on usvisascheduling after booking a slot?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "Yes. Bring both your original DS-160 confirmation page (used for scheduling) and your updated DS-160 confirmation page to your OFC biometric appointment to update it.",
              },
            },
            {
              "@type": "Question",
              name: "Does WeLivdIt assist with booking or scheduling US visa slots?",
              acceptedAnswer: {
                "@type": "Answer",
                text: "No. WeLivdIt is an independent F-1 visa interview preparation platform. We do not offer slot booking or appointment scheduling services. We specialize in 1-on-1 mock interviews, DS-160 audits, and profile strategy once candidates secure their dates.",
              },
            },
          ],
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com/" },
            {
              "@type": "ListItem",
              position: 2,
              name: "US Visa Scheduling Guide 2026",
              item: "https://welivdit.com/us-visa-scheduling-guide",
            },
          ],
        }),
      },
    ],
  }),
  component: ScheduleGuide,
});

const phases = [
  {
    tag: "Phase 01",
    icon: FileCheck2,
    title: "Submit DS-160",
    summary:
      "Start by completing and submitting your DS-160 form at ceac.state.gov. The officer reads this before you walk to the window, so every field must match your I-20 and future verbal answers.",
    checklist: [
      "Use the correct consular post - the one closest to your permanent address.",
      "Match SEVIS ID, university name, and program dates exactly to the I-20.",
      "Disclose every previous US visa, refusal, and US relative - non-negotiable.",
      "Save the Application ID before submitting - recovery is painful without it.",
      "Print the confirmation page with the barcode; this is the document the officer wants.",
    ],
  },
  {
    tag: "Phase 02",
    icon: CreditCard,
    title: "Start the visa application process in the US Visa Scheduling portal",
    summary:
      "Create your profile on the official US Visa Scheduling portal (usvisascheduling.com), generate a CGI reference, pay the MRV fee, and wait for the receipt to activate before you can book any appointments.",
    checklist: [
      "Sign up at usvisascheduling.com with a personal email you'll keep for 12+ months.",
      "Pick the correct visa class first - receipts are locked to one class.",
      "Generate the CGI reference number and pay via UPI, NEFT, Axis Bank cash, or debit card.",
      "Allow near-instant activation for UPI; 1 business day for NEFT; 2–4 working days for Axis Bank cash/waybill.",
      "Save the receipt PDF; you'll need the number to unlock the slot booking screen.",
    ],
  },
  {
    tag: "Phase 03",
    icon: CalendarClock,
    title: "Book the slots (OFC and consular)",
    summary:
      "Once the CGI receipt is live, the portal unlocks both OFC biometrics and the consular interview. Book in the right order to avoid date conflicts.",
    checklist: [
      "Book the consular interview first; then map the OFC at least 1–2 days earlier.",
      "Compare all 5 consulates (Chennai, Hyderabad, Mumbai, Kolkata, Delhi) for May–Aug F-1 rush.",
      "OFC and consular can be in different cities - plan travel before locking the slot.",
      "Slots release in waves at midnight, 8 AM, and 2 PM IST. Refresh, don't spam.",
      "Leave at least 2 weeks between booking and the interview for mocks and DS-160 review.",
      "Download both confirmation pages; phones are not allowed inside either appointment.",
    ],
  },
  {
    tag: "Phase 04",
    icon: ShieldCheck,
    title: "Pay the SEVIS fee",
    summary:
      "Pay the I-901 SEVIS fee on fmjfee.com only after your biometrics (OFC) appointment, and at least 3–5 business days before your actual consular interview date.",
    checklist: [
      "Locate your SEVIS ID on the top of your I-20 (begins with N00…).",
      "Pay the $350 F-1 / $220 J-1 fee on fmjfee.com using the same name as your passport.",
      "Wait 3 working days for the SEVIS record to sync to the DS-160 system.",
      "Download the printable receipt PDF - not the screenshot - and back it up to email + drive.",
      "Carry one printed copy to the consular interview; do not skip this step.",
    ],
  },
];

const mistakes = [
  {
    title: "Paying the MRV fee before finalising your visa class",
    body: "Each fee receipt is locked to one visa class. If you pay for B1/B2 and later realise you need F1, you'll pay again.",
  },
  {
    title: "Booking the consular interview before the DS-160 is final",
    body: "Your DS-160 confirmation number is what binds the appointment to you. Submitting a fresh DS-160 after booking can desync the records.",
  },
  {
    title: "Choosing an OFC city different from your consular city without planning travel",
    body: "Many applicants don't realise OFC and consular can be in different cities - and then scramble for flights at the last minute.",
  },
  {
    title: "Refreshing too aggressively and getting IP-blocked",
    body: "US visa scheduling portals have rate limiters. Use the portal at a sensible pace - getting locked out for 24 hours during slot season is painful.",
  },
];

const consulates = [
  {
    city: "Chennai",
    code: "CHN",
    vacAddress:
      "82, Good Shepherd Square, Kodambakkam High Rd, Badrikari, Tirumurthy Nagar, Nungambakkam, Chennai, Tamil Nadu 600034",
    consulateAddress: "Gemini Circle, Gopalapuram, Chennai, Tamil Nadu 600006",
    vacMap: "https://maps.google.com/?q=Good+Shepherd+VAC+Chennai",
    consulateMap: "https://share.google/fux5QpPwcETzyfJcC",
  },
  {
    city: "Hyderabad",
    code: "HYD",
    vacAddress:
      "Metro Station Hitech City, Lower Concourse, Hitech City Rd, Silicon Valley, Madhapur, Hyderabad, Telangana 500081",
    consulateAddress:
      "Survey No. 115, 1, Donald Trump Ave, Financial District, Nanakramguda, Hyderabad, Telangana 500032",
    vacMap: "https://share.google/UvLsSvjCeyeAlXp5o",
    consulateMap: "https://share.google/CSCHWuWrP5RXG06cO",
  },
  {
    city: "Kolkata",
    code: "KOL",
    vacAddress:
      "4th & 5th Floor, AA-I, Rene Towers, Plot No, Rajdanga Main Rd, Sector A, Kasba, Kolkata, West Bengal 700107",
    consulateAddress:
      "Suite-26, Cta Contemporary Training Academy, 5/1, Ho Chi Minh Sarani, Kankaria Estates, Park Street area, Kolkata, West Bengal 700071",
    vacMap: "https://share.google/YV2rw37uidW8bjwjc",
    consulateMap: "https://share.google/UpFYaUNGJnWnR1iQr",
  },
  {
    city: "Mumbai",
    code: "MUM",
    vacAddress:
      "Signia Pearl, C49, G Block Rd, G Block BKC, Bandra Kurla Complex, Bandra East, Mumbai, Maharashtra 400051",
    consulateAddress: "C-49, G-Block, Bandra Kurla Complex, Bandra East, Mumbai 400051",
    vacMap: "https://share.google/CGzlRdUcOpoFmtmNp",
    consulateMap: "https://share.google/hotEaUcfPux6fKn0B",
  },
  {
    city: "New Delhi",
    code: "DEL",
    vacAddress:
      "Mezzanine Level, The Eastern Court Janpath Metro Station, Baba Kharak Singh Rd, Shivaji Stadium, Connaught Place, New Delhi, Delhi 110001",
    consulateAddress: "Shantipath, Chanakyapuri, New Delhi 110021",
    vacMap: "https://share.google/peXTsaD1KSlJxCQ3p",
    consulateMap: "https://share.google/tSQ5GdZ9qAWi3S7wA",
  },
];

const commonErrors = [
  {
    title: "Session timed out / logged out repeatedly",
    steps: [
      "Close all portal tabs and clear browser cache/cookies for usvisascheduling.com.",
      "Open a fresh incognito/private window and log in again.",
      "Avoid using the back button; use only portal navigation links.",
      "If the issue persists, wait 15 minutes before retrying to clear server-side session locks.",
    ],
  },
  {
    title: "'Existing Username in Database' error",
    steps: [
      "This means the username is already taken, even if you previously deleted your account.",
      "Try a unique username with numbers or initials that aren't common names.",
      "If you believe it is your old account, use the 'Forgot Username' recovery flow.",
      "Still stuck? Contact the official US Travel Support centre - not a third-party agent.",
    ],
  },
  {
    title: "Payment made but MRV receipt not activating",
    steps: [
      "Verify you used the exact CGI reference number shown in your profile.",
      "UPI / QR payments usually reflect in minutes; NEFT can take up to 1 business day; Axis Bank cash/waybill typically takes 2–4 working days.",
      "Check your email receipt and bank statement for the transaction ID.",
      "After the expected wait, refresh the Receipts tab - do not pay twice unless instructed.",
    ],
  },
];

const ofcDocuments = [
  "Valid passport (current and all old passports, if any)",
  "DS-160 confirmation page used for scheduling",
  "Updated DS-160 confirmation page (if you changed it after booking)",
  "Visa appointment confirmation letter",
];

const consularDocuments = [
  "Valid Passport (current valid passport and ALL previous/old passports, if any)",
  "Visa Appointment Confirmation Letter & DS-160 Confirmation Page",
  "Signed Official I-20 Form & SEVIS Fee Receipt ($350 payment confirmation)",
  "University Admit Letter & Official Academic Documents (All Original Degree Certificates & Transcripts)",
  "Financial Source of Funds Documents (Bank Loan Sanction Letter, Fixed Deposits, Bank Statements, ITRs, etc.)",
  "Work Experience Documents (Joining Letter, Work Ex Certificate, Recent Payslips, & Company ID Card - if applicable)",
  "Physical Passport-Size Photographs (as per US Visa specifications) & Original Birth Certificate",
];

const quickNavItems = [
  { num: "01", label: "How to Login & Navigate usvisascheduling.com", href: "#login" },
  { num: "02", label: "Step-by-Step MRV Fee Payment Methods (UPI, NEFT, Axis Bank)", href: "#fees" },
  { num: "03", label: "The 4-Phase Scheduling Timeline", href: "#timeline" },
  { num: "04", label: "OFC Biometrics vs Consular Interview Slots", href: "#ofc-vs-consular" },
  { num: "05", label: "Common usvisascheduling Portal Errors & Fixes", href: "#errors" },
  { num: "06", label: "3 Non-Negotiable F-1 Requirements Checklist", href: "#mandatory" },
  { num: "07", label: "Documents to Carry for OFC & Consular Appointments", href: "#documents" },
  { num: "08", label: "5 Official US Consulate Locations in India & Maps", href: "#consulates" },
  { num: "09", label: "Costly Portal Scheduling Mistakes to Avoid", href: "#mistakes" },
  { num: "10", label: "Frequently Asked Questions (US Visa Scheduling)", href: "#faq" },
];

function scrollToHash(e: MouseEvent<HTMLAnchorElement>, href: string) {
  e.preventDefault();
  const id = href.replace("#", "");
  const el = document.getElementById(id);
  if (el) {
    el.scrollIntoView({ behavior: "smooth", block: "start" });
    history.pushState(null, "", href);
  }
}

function QuickNav() {
  return (
    <nav aria-label="Quick page navigation" className="rounded-xl border border-border bg-card p-5 sm:p-6 shadow-soft">
      <div className="flex items-center gap-2 mb-5">
        <span className="inline-flex items-center gap-1.5 rounded-full border border-primary/20 bg-primary/5 px-3 py-1.5 text-[11px] font-bold uppercase tracking-wider text-primary">
          <MapPin className="h-3.5 w-3.5" />
          On this page
        </span>
      </div>
      <ol className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {quickNavItems.map((item) => (
          <li key={item.href}>
            <a
              href={item.href}
              onClick={(e) => scrollToHash(e, item.href)}
              className="group flex items-start gap-3 rounded-xl border border-border bg-card/60 p-3.5 transition-all duration-200 hover:border-primary/30 hover:bg-card hover:shadow-sm hover:translate-x-1 focus:outline-hidden focus:ring-2 focus:ring-primary/30"
            >
              <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-[10px] font-bold text-muted-foreground tabular-nums">
                {item.num}
              </span>
              <span className="flex-1 text-sm font-medium text-foreground/90 group-hover:text-primary transition-colors">
                {item.label}
              </span>
              <ArrowRight className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground/50 transition-all duration-200 group-hover:translate-x-1 group-hover:text-primary" />
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function ScheduleGuide() {
  return (
    <>
      {/* Hero */}
      <section className="bg-gradient-hero">
        <div className="mx-auto max-w-4xl px-5 pt-16 pb-12 md:pt-24 md:pb-16">
          <p className="text-sm font-medium text-primary uppercase tracking-wider">
            Visa Scheduling Guide · India · 2026
          </p>
          <h1 className="mt-3 font-display text-4xl sm:text-5xl md:text-6xl font-semibold tracking-tight text-balance leading-[1.05]">
            US Visa Scheduling Portal Guide 2026: Login, Fee Payment & Slot Rules{" "}
            <em className="not-italic text-primary">(India)</em>
          </h1>
          <p className="mt-5 text-foreground/80 text-base sm:text-lg max-w-2xl leading-relaxed">
            Every year, thousands of Indian applicants lose weeks to scheduling mistakes on usvisascheduling.com. This
            is the no-fluff walkthrough we wish every student had before they started.
          </p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/find-your-plan"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Find your prep plan <ArrowRight className="h-4 w-4" />
            </Link>
            <a
              href={site.whatsapp}
              target="_blank"
              rel="nofollow noopener noreferrer"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-border bg-white/70 text-foreground font-medium hover:bg-white transition"
            >
              Ask us on WhatsApp
            </a>
          </div>
        </div>
      </section>

      {/* Important Notice */}
      <section id="notice" className="mx-auto max-w-4xl px-5 -mt-4">
        <div className="rounded-2xl border border-amber-200/60 bg-amber-50/70 dark:bg-amber-950/20 dark:border-amber-900/40 p-5 sm:p-6 flex gap-4 items-start shadow-soft">
          <div className="mt-0.5 shrink-0">
            <Lightbulb className="h-5 w-5 text-amber-600 dark:text-amber-400" />
          </div>
          <div>
            <h2 className="font-display text-base sm:text-lg font-semibold text-foreground">
              Important Notice: Independent Interview Coaching Platform
            </h2>
            <p className="mt-2 text-sm sm:text-base text-foreground/85 leading-relaxed">
              <strong>Please Note:</strong> WeLivdIt is an independent F-1 visa interview preparation platform. We do{" "}
              <em>not</em> book, reschedule, or assist with US visa appointment slots. This guide is purely educational
              to help applicants navigate official portals independently.
            </p>
          </div>
        </div>
      </section>

      {/* Table of contents */}
      <section className="mx-auto max-w-4xl px-5 mt-10">
        <QuickNav />
      </section>

      {/* Login & Navigate */}
      <section id="login" className="mx-auto max-w-3xl px-5 mt-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          How to Login & Navigate usvisascheduling.com (India Portal)
        </h2>
        <p className="mt-4 text-foreground/85 leading-relaxed">
          <a
            href="https://www.usvisascheduling.com"
            target="_blank"
            rel="noreferrer"
            className="text-primary hover:underline"
          >
            usvisascheduling.com
          </a>{" "}
          is the official portal Indian applicants use to schedule non-immigrant visa appointments. It is separate from
          the DS-160 form at ceac.state.gov and the SEVIS fee portal at fmjfee.com.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {[
            {
              icon: Monitor,
              title: "Create a unique account",
              text: "Use a personal email you'll keep for at least a year. Pick a username that isn't a common name to avoid 'Existing Username in Database' errors.",
            },
            {
              icon: Lock,
              title: "Verify & secure your profile",
              text: "Confirm your email and set your security questions. You cannot recover a forgotten username without them.",
            },
            {
              icon: FileText,
              title: "Add DS-160 & visa class",
              text: "Enter your DS-160 confirmation number and select the correct visa class before paying any fee - receipts are locked to one class.",
            },
            {
              icon: MapPin,
              title: "Pick your post",
              text: "Choose from Chennai, Hyderabad, Kolkata, Mumbai, or New Delhi. You can book OFC and consular appointments in different cities if travel is feasible.",
            },
          ].map((item) => (
            <div key={item.title} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <item.icon className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-display text-base font-semibold">{item.title}</h3>
              <p className="mt-2 text-sm text-foreground/80 leading-relaxed">{item.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Fees */}
      <section id="fees" className="mx-auto max-w-3xl px-5 mt-14">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Step-by-Step MRV Fee Payment Methods (UPI, NEFT, Axis Bank)
        </h2>
        <p className="mt-4 text-foreground/85 leading-relaxed">
          The Machine Readable Visa (MRV) fee must be paid before the portal unlocks appointment booking. For most
          Indian F-1, B1/B2, and J-1 applicants, the fee is <strong>$185 USD</strong>, collected in INR at the portal's
          exchange rate.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-3">
          {[
            { icon: Smartphone, label: "UPI / QR code", value: "Instant", note: "Fastest activation" },
            { icon: RefreshCw, label: "NEFT transfer", value: "≤ 1 day", note: "Use exact CGI reference" },
            { icon: Banknote, label: "Axis Bank cash", value: "2–4 days", note: "Waybill reconciliation" },
          ].map((f) => (
            <div key={f.label} className="rounded-2xl border border-border bg-card p-5 shadow-soft">
              <f.icon className="h-5 w-5 text-primary" />
              <div className="mt-3 text-xs uppercase tracking-wider text-muted-foreground">{f.label}</div>
              <div className="mt-1 font-display text-2xl font-semibold">{f.value}</div>
              <div className="mt-1 text-xs text-foreground/70">{f.note}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-primary" />
            UPI vs NEFT fee activation explained
          </h3>
          <ul className="mt-4 space-y-3 text-sm text-foreground/85 leading-relaxed">
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong>UPI / VFS QR code:</strong> Scan the QR shown on the portal, complete payment in your bank app,
                and return to the portal. Receipt activation is usually near-instant, often within minutes.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong>NEFT / RTGS:</strong> Copy the portal's bank details and beneficiary exactly. Transfers can take
                up to 1 business day to reflect. Do not book slots until the receipt shows "Paid".
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
              <span>
                <strong>Axis Bank cash:</strong> Print the deposit slip/waybill from the portal, pay cash at an Axis
                Bank branch, and keep the stamped copy safe. While portal documentation states 1 business day, cash
                deposit and waybill reconciliations routinely take 2 to 4 working days to reflect as "Paid" on
                usvisascheduling.com.
              </span>
            </li>
          </ul>
        </div>

        <p className="mt-4 text-sm text-muted-foreground">
          Fees are quoted in USD and collected in INR at the portal's exchange rate. Always confirm the current amount
          on the official portal before paying - this guide is a reference, not a substitute for the official site.
        </p>
      </section>

      {/* DS-160 mismatch tip */}
      <section className="mx-auto max-w-3xl px-5 mt-10">
        <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <HelpCircle className="h-4 w-4 text-primary" />
            DS-160 Confirmation Mismatch Fix
          </h3>
          <p className="mt-3 text-sm sm:text-base text-foreground/85 leading-relaxed">
            If you update your DS-160 form <em>after</em> booking a slot, you do <strong>not</strong> need to cancel
            your appointment. Instead, carry <strong>both</strong> the original DS-160 confirmation page (used to
            schedule) and the new, updated DS-160 confirmation page to your OFC biometrics appointment. The VAC staff
            can update the DS-160 number linked to your appointment before the consular interview.
          </p>
        </div>
      </section>

      {/* Phases - interactive timeline */}
      <section id="timeline" className="mx-auto max-w-4xl px-5 mt-16">
        <div className="flex items-baseline justify-between gap-6">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            The 4-phase scheduling timeline
          </h2>
          <span
            className="hidden sm:inline text-[11px] uppercase text-muted-foreground"
            style={{ letterSpacing: "0.22em" }}
          >
            Tap a phase to expand
          </span>
        </div>
        <p className="mt-3 text-foreground/75 max-w-2xl leading-relaxed">
          A clean, linear sequence - skip a phase and the next one quietly fails. Click any phase to open its full
          checklist.
        </p>

        <ol className="mt-10 relative">
          {/* vertical track */}
          <span aria-hidden className="absolute left-[19px] sm:left-[23px] top-2 bottom-2 w-px bg-border" />
          {phases.map((p, i) => (
            <PhaseRow key={p.tag} phase={p} index={i} />
          ))}
        </ol>
      </section>

      {/* OFC vs Consular */}
      <section id="ofc-vs-consular" className="mx-auto max-w-4xl px-5 mt-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          OFC Biometric vs Consular Interview Slot Differences
        </h2>
        <p className="mt-4 text-foreground/85 leading-relaxed max-w-3xl">
          The portal books two separate appointments. Understanding the difference keeps you from showing up at the
          wrong location or on the wrong date.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="flex items-center gap-2 text-primary">
              <ClipboardList className="h-5 w-5" />
              <h3 className="font-display text-lg font-semibold">OFC Biometrics</h3>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-foreground/85">
              <li>· Photo & fingerprint capture</li>
              <li>· Usually at a Visa Application Center (VAC)</li>
              <li>· Carry passport, DS-160 confirmation(s), and appointment letter</li>
            </ul>
          </div>
          <div className="rounded-2xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-6 shadow-soft">
            <div className="flex items-center gap-2 text-primary">
              <FileCheck2 className="h-5 w-5" />
              <h3 className="font-display text-lg font-semibold">Consular Interview</h3>
            </div>
            <ul className="mt-4 space-y-2 text-sm text-foreground/85">
              <li>· Visa officer interview</li>
              <li>· Held at the US Embassy or Consulate</li>
              <li>· Book only after OFC slot is locked</li>
              <li>· Bring financials, I-20, SEVIS receipt, and supporting documents</li>
            </ul>
          </div>
        </div>

        {/* Conversion CTA card */}
        <div className="mt-8 rounded-3xl bg-gradient-hero border border-border p-8 sm:p-10 relative overflow-hidden">
          <div className="relative z-10 max-w-2xl">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium uppercase tracking-wider">
              Got your F-1 visa interview date locked in?
            </span>
            <h3 className="mt-4 font-display text-2xl sm:text-3xl font-semibold tracking-tight">
              Don't risk a 214(b) rejection on interview day.
            </h3>
            <p className="mt-3 text-foreground/85 leading-relaxed">
              Practice real visa-officer scenarios, audit your DS-160 narrative, and build confidence before standing at
              the consular counter.
            </p>
            <div className="mt-6">
              <Link
                to="/pricing"
                className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
              >
                Explore F-1 Mock & Strategy Plans (₹3,000 – ₹4,500) <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Common errors */}
      <section id="errors" className="mx-auto max-w-4xl px-5 mt-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Common usvisascheduling Portal Errors & Step-by-Step Fixes
        </h2>
        <p className="mt-4 text-foreground/85 leading-relaxed">
          Most portal errors come from browser state, duplicate accounts, or payment timing. Use the fixes below before
          panicking or paying twice.
        </p>
        <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {commonErrors.map((e) => (
            <div key={e.title} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wider">Error</span>
              </div>
              <h3 className="mt-2 font-display text-lg font-semibold">{e.title}</h3>
              <ol className="mt-3 space-y-2 text-sm text-foreground/85 leading-relaxed list-decimal list-inside">
                {e.steps.map((s, i) => (
                  <li key={i}>{s}</li>
                ))}
              </ol>
            </div>
          ))}
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <div className="flex items-center gap-2 text-primary">
              <RefreshCw className="h-4 w-4" />
              <span className="text-xs font-medium uppercase tracking-wider">Browser</span>
            </div>
            <h3 className="mt-2 font-display text-lg font-semibold">Cache / incognito switching</h3>
            <p className="mt-2 text-sm text-foreground/85 leading-relaxed">
              If the portal behaves oddly, clear cache and cookies for usvisascheduling.com, then reopen in an
              incognito/private window. Disable ad blockers and VPNs that may interfere with session cookies.
            </p>
          </div>
        </div>
      </section>

      {/* Mandatory F-1 Requirements */}
      <section id="mandatory" className="mx-auto max-w-4xl px-5 mt-10">
        <div className="rounded-2xl border-l-4 border-l-red-600 bg-red-50/70 p-5 sm:p-6 shadow-soft dark:bg-red-950/20">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-red-100 px-2.5 py-1 text-xs font-bold uppercase tracking-wider text-red-700 dark:bg-red-900/40 dark:text-red-200">
            🛑 MANDATORY F-1 CHECKLIST
          </span>
          <h2 className="mt-3 font-display text-lg sm:text-xl font-semibold text-foreground">
            3 Non-Negotiable F-1 Requirements
          </h2>
          <ul className="mt-4 space-y-3 text-sm sm:text-base text-foreground/85 leading-relaxed">
            <li className="flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0 font-bold text-red-600">1.</span>
              <span>
                <strong>DS-160 Match:</strong> Your DS-160 Application ID on the appointment confirmation MUST match
                your profile. If updated after booking, carry BOTH original and new confirmation pages to the OFC.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0 font-bold text-red-600">2.</span>
              <span>
                <strong>Signed I-20 &amp; SEVIS:</strong> Your physical I-20 must be signed in ink by you and your DSO.
                Pay the SEVIS fee ($350) on fmjfee.com at least 3 days before the consular interview.
              </span>
            </li>
            <li className="flex items-start gap-2.5">
              <span className="mt-0.5 shrink-0 font-bold text-red-600">3.</span>
              <span>
                <strong>Separate Locations:</strong> VAC (Biometrics) and Consular Interviews are held at DIFFERENT
                addresses. Check the location cards below before traveling.
              </span>
            </li>
          </ul>
        </div>
      </section>

      {/* Documents */}
      <section id="documents" className="mx-auto max-w-4xl px-5 mt-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          What Documents to Carry to Your OFC & Consular Appointments
        </h2>
        <p className="mt-4 text-foreground/85 leading-relaxed">
          Print everything the night before. Phones and electronic devices are usually not allowed inside either
          appointment.
        </p>
        <div className="mt-6 grid gap-6 sm:grid-cols-2">
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <ClipboardList className="h-5 w-5 text-primary" />
              OFC Biometrics Appointment
            </h3>
            <ul className="mt-4 space-y-2">
              {ofcDocuments.map((d) => (
                <li key={d} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 shadow-soft">
            <h3 className="font-display text-lg font-semibold flex items-center gap-2">
              <FileCheck2 className="h-5 w-5 text-primary" />
              Consular Interview Appointment
            </h3>
            <ul className="mt-4 space-y-2">
              {consularDocuments.map((d) => (
                <li key={d} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
                  <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <span>{d}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* 5 consulates */}
        <div id="consulates" className="mt-8 rounded-2xl border border-border bg-card p-6 shadow-soft">
          <h3 className="font-display text-lg font-semibold flex items-center gap-2">
            <MapPin className="h-5 w-5 text-primary" />5 Official US Consulate Locations in India
          </h3>
          <div className="mt-4 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {consulates.map((c) => (
              <div key={c.city} className="rounded-xl border border-border bg-background p-4">
                <div className="flex items-center justify-between">
                  <div className="font-display font-semibold text-foreground">{c.city}</div>
                  <div className="text-xs text-muted-foreground uppercase tracking-wider">{c.code}</div>
                </div>
                <div className="mt-3 space-y-3">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">VAC / OFC</p>
                    <p className="mt-1 text-xs text-foreground/80 leading-relaxed">{c.vacAddress}</p>
                    <a
                      href={c.vacMap}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      <MapPin className="h-3 w-3" /> Open in Google Maps
                    </a>
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-wider text-primary">Consulate / Embassy</p>
                    <p className="mt-1 text-xs text-foreground/80 leading-relaxed">{c.consulateAddress}</p>
                    <a
                      href={c.consulateMap}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-1.5 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
                    >
                      <MapPin className="h-3 w-3" /> Open in Google Maps
                    </a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mistakes */}
      <section id="mistakes" className="mx-auto max-w-4xl px-5 mt-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">Common mistakes to avoid</h2>
        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          {mistakes.map((m) => (
            <div key={m.title} className="rounded-2xl border border-border bg-card p-6 shadow-soft">
              <div className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="h-4 w-4" />
                <span className="text-xs font-medium uppercase tracking-wider">Mistake</span>
              </div>
              <h3 className="mt-2 font-display text-lg font-semibold">{m.title}</h3>
              <p className="mt-2 text-sm text-foreground/85 leading-relaxed">{m.body}</p>
            </div>
          ))}
        </div>
      </section>

      <SchedulingTroubleshooting />

      {/* FAQ */}
      <section id="faq" className="mx-auto max-w-3xl px-5 mt-16">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
          Frequently Asked Questions (US Visa Scheduling India)
        </h2>
        <div className="mt-6 space-y-5">
          {[
            {
              q: "How do I log in to the US visa scheduling portal in India?",
              a: "Indian applicants must use the official portal at usvisascheduling.com. Create an account with a unique username, verify your email, and set security questions to access the dashboard.",
            },
            {
              q: "How long does MRV fee activation take via UPI payment?",
              a: "UPI payments through the official usvisascheduling portal reflect instantly in most cases. NEFT bank transfers typically take up to 1 business day. Axis Bank cash deposits and waybills routinely take 2 to 4 working days to reflect as 'Paid' on usvisascheduling.com.",
            },
            {
              q: "Can I update my DS-160 number on usvisascheduling after booking a slot?",
              a: "Yes. Bring both your original DS-160 confirmation page (used for scheduling) and your updated DS-160 confirmation page to your OFC biometric appointment to update it.",
            },
            {
              q: "Does WeLivdIt assist with booking or scheduling US visa slots?",
              a: "No. WeLivdIt is an independent F-1 visa interview preparation platform. We do not offer slot booking or appointment scheduling services. We specialize in 1-on-1 mock interviews, DS-160 audits, and profile strategy once candidates secure their dates.",
            },
          ].map((f) => (
            <details key={f.q} className="group rounded-2xl border border-border bg-card p-5 shadow-soft">
              <summary className="cursor-pointer list-none flex items-center justify-between font-display text-base font-semibold">
                <span>{f.q}</span>
                <span className="ml-4 text-primary transition group-open:rotate-45">+</span>
              </summary>
              <p className="mt-3 text-sm text-foreground/85 leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="mx-auto max-w-6xl px-5 py-16 md:py-24">
        <div className="rounded-[2rem] bg-gradient-hero border border-border p-8 sm:p-10 md:p-16 text-center">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-medium uppercase tracking-wider">
            🎓 Verified Proof: 423+ F-1 Visas Approved (350 Fall 2026 + 73 Spring/Fall 2027)
          </span>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance">
            Slot booked. What's next?
          </h2>
          <p className="mt-4 text-foreground/80 max-w-xl mx-auto">
            Now the real work begins — DS-160 review, 1-on-1 mock interviews, document verification, and your core
            interview narrative. Join hundreds of successful students this intake.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/pricing"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-gradient-cta text-primary-foreground font-medium shadow-glow hover:opacity-90 transition"
            >
              Find your prep plan <ArrowRight className="h-4 w-4" />
            </Link>
            <Link
              to="/services"
              className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-border bg-white/70 text-foreground font-medium hover:bg-white transition"
            >
              See all services
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

function PhaseRow({ phase, index }: { phase: (typeof phases)[number]; index: number }) {
  const [open, setOpen] = useState(index === 0);
  const Icon = phase.icon;
  return (
    <li className="relative pl-12 sm:pl-16 pb-6">
      {/* node */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`phase-${index}-panel`}
        className="absolute left-0 top-3 h-10 w-10 sm:h-12 sm:w-12 rounded-full bg-card border border-border grid place-items-center text-primary shadow-soft hover:border-primary/60 transition-colors"
      >
        <Icon className="h-4 w-4 sm:h-5 sm:w-5" />
      </button>

      <div
        className={`rounded-2xl border bg-card shadow-soft transition-colors ${
          open ? "border-primary/40" : "border-border"
        } ${index % 2 === 1 ? "sm:ml-4" : ""}`}
      >
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="w-full text-left p-5 sm:p-6 flex items-start justify-between gap-4"
        >
          <div className="min-w-0">
            <div
              className="text-[10px] sm:text-[11px] font-medium text-primary uppercase"
              style={{ letterSpacing: "0.28em" }}
            >
              {phase.tag}
            </div>
            <h3 className="mt-2 font-display text-lg sm:text-xl font-semibold leading-snug">{phase.title}</h3>
            <p className="mt-2 text-sm text-foreground/75 leading-relaxed">{phase.summary}</p>
          </div>
          <motion.span
            animate={{ rotate: open ? 45 : 0 }}
            transition={{ type: "spring", stiffness: 260, damping: 22, mass: 0.9 }}
            className="shrink-0 h-8 w-8 rounded-full border border-border grid place-items-center text-primary"
            aria-hidden
          >
            <Plus className="h-4 w-4" />
          </motion.span>
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              id={`phase-${index}-panel`}
              key="content"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{
                type: "spring",
                stiffness: 180,
                damping: 24,
                mass: 1.1,
                opacity: { duration: 0.25 },
              }}
              className="overflow-hidden"
            >
              <div className="px-5 sm:px-6 pb-6">
                <div className="border-t border-border pt-5">
                  <div
                    className="text-[10px] font-medium text-muted-foreground uppercase"
                    style={{ letterSpacing: "0.24em" }}
                  >
                    Checklist
                  </div>
                  <ul className="mt-3 space-y-2.5">
                    {phase.checklist.map((c) => (
                      <li key={c} className="flex items-start gap-2.5 text-sm text-foreground/85 leading-relaxed">
                        <CheckCircle2 className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                        <span>{c}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </li>
  );
}
